import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtDate, priorityColor, statusColor } from "@/lib/utils-format";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["todo", "in_progress", "done", "blocked"];
const PRIORITIES = ["low", "medium", "high", "urgent"];

const empty = { title: "", description: "", assigned_to: "", priority: "medium", status: "todo", due_date: "" };

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [t, u] = await Promise.all([api.get("/tasks"), api.get("/users")]);
    setTasks(t.data); setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    try {
      await api.post("/tasks", { ...form, assigned_to: form.assigned_to || null });
      toast.success("Task created");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const changeStatus = async (t, status) => { await api.patch(`/tasks/${t.id}`, { status }); load(); };
  const remove = async (t) => {
    if (!window.confirm("Delete task?")) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Work</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Tasks</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="task-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle className="font-[Outfit]">New Task</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Assigned to</Label>
                <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="task-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STATUSES.map((st) => {
          const items = tasks.filter((t) => t.status === st);
          return (
            <div key={st} data-testid={`task-col-${st}`} className="kanban-col rounded-md p-3">
              <div className="flex items-center justify-between mb-3">
                <Badge className={`capitalize ${statusColor(st)}`} variant="outline">{st.replace("_", " ")}</Badge>
                <span className="text-xs text-[#8B7355]">{items.length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {items.map((t) => {
                  const usr = users.find((u) => u.id === t.assigned_to);
                  return (
                    <div key={t.id} data-testid={`task-card-${t.id}`} className="bg-white border border-[#EAE5D9] rounded-md p-3 hover-lift">
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm">{t.title}</div>
                        <button onClick={() => remove(t)} className="text-red-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      {t.description && <p className="text-xs text-[#5C4033] mt-1 line-clamp-2">{t.description}</p>}
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`capitalize text-[10px] ${priorityColor(t.priority)}`}>{t.priority}</Badge>
                        {t.due_date && <span className="text-[10px] text-[#8B7355]">Due {fmtDate(t.due_date)}</span>}
                        {usr && <span className="text-[10px] text-[#8B7355]">· {usr.name}</span>}
                      </div>
                      <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                        <SelectTrigger className="h-7 mt-2 text-xs" data-testid={`task-status-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white">
                          {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize text-xs">{s.replace("_", " ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
                {items.length === 0 && <div className="text-xs text-[#8B7355] italic text-center py-4">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
